/**
 * Authentication utilities for the loader
 */

import { debug } from "./utils/debug";
import { calculateBodySHA256 } from "./utils/crypto";

export interface AuthRequest {
  origin?: string;
  restoreSessionId?: string;
  additionalInfo?: Record<string, string>;
}

export interface AuthResponse {
  sessionToken: string;
  sessionId: string;
  newSession: boolean;
  greeting?: string;
  chatHistory?: Array<{
    type: "human" | "ai";
    content: string;
  }>;
  tokenTtlMinutes: number;
  eventsConfig?: {
    httpEndpoint: string;
    realtimeEndpoint: string;
  };
}

export interface AuthValidationResponse {
  isValid: boolean;
  greeting?: string;
  logoUrl?: string;
  logoAlt?: string;
}

export class AuthenticationError extends Error {
  constructor(public status: number, public details: any, message?: string) {
    super(message || `Authentication failed: ${status}`);
    this.name = "AuthenticationError";
  }
}

export async function validateWidget(
  apiKey: string
): Promise<AuthValidationResponse> {
  const authEndpoint = import.meta.env.VITE_WIDGET_AUTH_ENDPOINT;

  if (!authEndpoint) {
    throw new Error("VITE_WIDGET_AUTH_ENDPOINT not configured");
  }

  if (!apiKey) {
    throw new Error("API key is required");
  }

  debug.log("[Apten Loader] 🔐 Validating widget API key...", {
    endpoint: authEndpoint,
    apiKey: apiKey.substring(0, 10) + "...", // Log partial key for debugging
    domain: window.location.hostname,
  });

  const requestBody: AuthRequest = {
    origin: window.location.origin,
  };

  try {
    const bodyString = JSON.stringify(requestBody);
    const contentSha256 = await calculateBodySHA256(bodyString);

    const response = await fetch(authEndpoint, {
      method: "POST",
      headers: {
        "X-Webchat-API-Key": apiKey,
        "Content-Type": "application/json",
        "x-amz-content-sha256": contentSha256,
      },
      body: bodyString,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new AuthenticationError(response.status, errorData);
    }

    const validationResponse: AuthValidationResponse = await response.json();

    debug.log("[Apten Loader] ✅ Widget validation successful:", {
      isValid: validationResponse.isValid,
      hasLogo: !!validationResponse.logoUrl,
    });

    return validationResponse;
  } catch (error) {
    if (error instanceof AuthenticationError) {
      debug.error("[Apten Loader] ❌ Widget validation failed:", error);
      throw error;
    }

    debug.error("[Apten Loader] ❌ Network error during validation:", error);
    throw new AuthenticationError(0, { originalError: error }, "Network error");
  }
}

export async function createSession(
  apiKey: string,
  restoreSessionId?: string
): Promise<AuthResponse> {
  const sessionEndpoint = import.meta.env.VITE_WIDGET_SESSION_ENDPOINT;

  if (!sessionEndpoint) {
    throw new Error("VITE_WIDGET_SESSION_ENDPOINT not configured");
  }

  if (!apiKey) {
    throw new Error("API key is required");
  }

  debug.log("[Apten Loader] 🔐 Creating session...", {
    endpoint: sessionEndpoint,
    apiKey: apiKey.substring(0, 10) + "...", // Log partial key for debugging
    domain: window.location.hostname,
    restoreSessionId: restoreSessionId || "none",
  });

  const requestBody: AuthRequest = {
    origin: window.location.origin,
    ...(restoreSessionId && { restoreSessionId }),
  };

  try {
    const bodyString = JSON.stringify(requestBody);
    const contentSha256 = await calculateBodySHA256(bodyString);

    const response = await fetch(sessionEndpoint, {
      method: "POST",
      headers: {
        "X-Webchat-API-Key": apiKey,
        "Content-Type": "application/json",
        "x-amz-content-sha256": contentSha256,
      },
      body: bodyString,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new AuthenticationError(response.status, errorData);
    }

    const authResponse: AuthResponse = await response.json();

    debug.log("[Apten Loader] ✅ Session response:", {
      sessionId: authResponse.sessionId,
      newSession: authResponse.newSession,
      hasHistory: !!authResponse.chatHistory?.length,
      tokenTtlMinutes: authResponse.tokenTtlMinutes,
      tokenPreview: authResponse.sessionToken.substring(0, 20) + "...",
    });

    return authResponse;
  } catch (error) {
    if (error instanceof AuthenticationError) {
      debug.error("[Apten Loader] ❌ Session creation failed:", error);
      throw error;
    }

    debug.error(
      "[Apten Loader] ❌ Network error during session creation:",
      error
    );
    throw new AuthenticationError(0, { originalError: error }, "Network error");
  }
}

/**
 * Signal session as INACTIVE on page unload.
 * Synchronous fire-and-forget — uses fetch with keepalive: true.
 * Accepts pre-computed SHA256 to avoid async work in pagehide handler.
 */
export function signalInactive(
  sessionToken: string,
  contentSha256: string
): void {
  const endpoint = import.meta.env.VITE_WIDGET_UPDATE_STATUS_ENDPOINT;
  if (!endpoint) return;

  const bodyString = JSON.stringify({ sessionStatus: "INACTIVE" });

  fetch(endpoint, {
    method: "POST",
    headers: {
      "x-webchat-jwt-token": sessionToken,
      "Content-Type": "application/json",
      "x-amz-content-sha256": contentSha256,
    },
    body: bodyString,
    keepalive: true,
  }).catch(() => {});
}

export async function endSession(sessionToken: string): Promise<void> {
  const endSessionEndpoint = import.meta.env.VITE_WIDGET_END_SESSION_ENDPOINT;

  if (!endSessionEndpoint) {
    debug.warn(
      "[Apten Loader] VITE_WIDGET_END_SESSION_ENDPOINT not configured - skipping backend session cleanup"
    );
    return;
  }

  debug.log("[Apten Loader] 🔚 Ending session...", {
    endpoint: endSessionEndpoint,
    tokenPreview: sessionToken.substring(0, 20) + "...",
  });

  try {
    const bodyString = JSON.stringify({});
    const contentSha256 = await calculateBodySHA256(bodyString);

    const response = await fetch(endSessionEndpoint, {
      method: "POST",
      headers: {
        "x-webchat-jwt-token": sessionToken,
        "Content-Type": "application/json",
        "x-amz-content-sha256": contentSha256,
      },
      body: bodyString,
    });

    if (!response.ok) {
      debug.warn(
        "[Apten Loader] ⚠️ Failed to end session on backend:",
        response.status
      );
    } else {
      debug.log("[Apten Loader] ✅ Session ended successfully");
    }
  } catch (error) {
    debug.warn("[Apten Loader] ⚠️ Network error ending session:", error);
  }
}
